import React from 'react';
import moment from 'moment';
import { Field } from 'redux-form';

import Counter, { BoundedLogic } from 'components/Input/Counter';
import Postfix from 'components/Input/Postfix';
import { translate } from 'helpers/Translate';
import Apartment from 'svg/Apartment';
import Duplex from 'svg/Duplex';
import Hotel from 'svg/Hotel';
import House from 'svg/House';
import Loft from 'svg/Loft';
import Triplex from 'svg/Triplex';

/**
 * gaCategory
 * @type {string}
 */
export const GA_PAGE_CATEGORY = 'estima';

/**
 * One to ten logic for room count.
 * @type {Object}
 */
export const OneToTenLogic = BoundedLogic(1, 10);

/**
 * One to two logic for room count.
 * @type {Object}
 */
export const OneToTwoLogic = BoundedLogic(1, 2);

/**
 * Base estima app URL.
 * @type {String}
 */
export const ESTIMA_BASE_URL = '/estimation-immobiliere';

/**
 * Barometre URL.
 * @type {String}
 */
export const BAROMETRE_URL = '/update';

/**
 * Base habitation valuation app URL.
 * @type {String}
 */
export const HABITATION_VALUATION_URL = '/mon-compte/biens-estimes';

/**
 * Event Category of whole estima app.
 * @type {String}
 */
export const GA_ESTIMA_FORM_CATEGORY = 'Formulaire Estima';

/**
 * Event Category of whole estima app.
 * @type {Number}
 */
export const ESTIMA_FUNNEL_CONFIRMATION_DELAY = 2000;

/**
 * Event Category of whole app.
 * @type {String}
 */
export const GA_UPDATE_CATEGORY = 'Barometre Estima';

/**
 * Mobile breakpoint.
 * @type {String}
 */
export const MOBILE_BREAKBOINT = '320px';

/**
 * Tablet breakpoint.
 * @type {String}
 */
export const TABLET_BREAKBOINT = '750px';

/**
 * Estima form name.
 * @type {String}
 */
export const ESTIMA_FORM = 'ESTIMA_FORM';

/**
 * Name of Step Number if values.
 * @type {String}
 */
export const STEP_NUMBER_NAME = '_stepNumber';

/**
 * Name of Step Id if values.
 * @type {String}
 */
export const STEP_ID_NAME = '_stepId';

/**
 * Input field prefix in form.
 * @type {String}
 */
export const LOGIN_INPUT_PREFIX = '_login_';

/**
 * Estima form address field name.
 * @type {String}
 */
export const FORM_ADDRESS_NAME = 'address_name';

/**
 * Estima form city field name.
 * @type {String}
 */
export const FORM_CITY_NAME = 'city_name';

/**
 * hint enum.
 * @type {String}
 */
export const HINT_EXISTING_EMAIL = 'existing_email';

/**
 * hint enum.
 * @type {String}
 */
export const HINT_INVALID_EMAIL = 'invalid_email';

/**
 * Estima form zip field name.
 * @type {String}
 */
export const FORM_ZIP = 'zip';

/**
 * Regex to check validity of email
 * @type {RegExp}
 */
export const EMAIL_REGEX =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/**
 * Regex to check validity of a postal code
 * @type {RegExp}
 */
export const ZIP_REGEX = /^[0-9]{5}$/;

/**
 * Signup succeeded flag.
 * @type {String}
 */
export const SIGNUP_SUCCEEDED = '_signup_succeeded';

/**
 * User input debounce timeout.
 * @type {Number}
 */
export const DEBOUNCE_TIMEOUT = 300;

/**
 * Error display duration.
 * @type {Number}
 */
export const ERROR_DURATION = 6000;

/**
 * Action name prefix for identifying error case.
 * @type {RegExp}
 */
export const FAILED_API_REQUEST_PREFIX = 'FAILED_';

/**
 * Item Type for House
 * @type {String}
 */
export const ITEM_TYPE_HOUSE = 'ITEM_TYPE.HOUSE';

/**
 * Item Type for Apartment
 * @type {String}
 */
export const ITEM_TYPE_APARTMENT = 'ITEM_TYPE.APARTMENT';

/**
 * For Levenshtein algorithm between 2 addresses
 * @type {number}
 */
export const MAX_ADDRESS_WORD_DISTANCE = 10;

/**
 * Available project types.
 * @type {Array}
 */
export const PROPERTY_TYPES = [
    {
        id: 'house',
        type: ITEM_TYPE_HOUSE,
        subType: '',
        label: translate('Maison'),
        icon: <House />,
    },
    {
        id: 'apartment',
        type: ITEM_TYPE_APARTMENT,
        subType: '',
        label: translate('Appartement'),
        icon: <Apartment />,
    },
    {
        id: 'duplex',
        type: ITEM_TYPE_APARTMENT,
        subType: 'ITEM_SUBTYPE.DUPLEX',
        label: translate('Duplex'),
        icon: <Duplex />,
    },
    {
        id: 'triplex',
        type: ITEM_TYPE_APARTMENT,
        subType: 'ITEM_SUBTYPE.TRIPLEX',
        label: translate('Triplex'),
        icon: <Triplex />,
    },
    {
        id: 'loft',
        type: ITEM_TYPE_APARTMENT,
        subType: 'ITEM_SUBTYPE.LOFT',
        label: translate('Loft / Atelier'),
        icon: <Loft />,
    },
    {
        id: 'hotel',
        type: ITEM_TYPE_HOUSE,
        subType: 'ITEM_SUBTYPE.PRIVATEMANSION',
        label: translate('Hotel particulier'),
        icon: <Hotel />,
    },
];

/**
 * Options to choose the level of a house.
 * @type {Array}
 */
export const LEVEL_COUNT_OPTIONS = [
    { index: 1, value: 0, label: 'Plain-pied' },
    { index: 2, value: 1, label: '2' },
    { index: 3, value: 2, label: '3' },
    { index: 4, value: 3, label: '4+' },
];

/**
 * array of all the item_type/item_subtype used for the group ITEM_TYPE.HOUSE
 * @type {Array}
 */
export const PROPERTY_HOUSE_GROUP = ['house', 'hotel'];

/**
 * array of all the item_type/item_subtype used for the group ITEM_TYPE.APARTMENT
 * @type {Array}
 */
export const PROPERTY_APARTMENT_GROUP = ['apartment', 'duplex', 'triplex', 'loft'];

/**
 * EstimaForm featuresType use for the step optional features.
 * @type {Array}
 */
export const FEATURES_TYPES = [
    {
        id: 'shared_wall',
        label: translate('Murs mitoyens'),
        subField: {
            id: 'shared_wall_count',
            component: (
                <Field
                    component={Counter}
                    fieldLogic={OneToTwoLogic}
                    name="shared_wall_count"
                    small
                />
            ),
        },
        group: PROPERTY_HOUSE_GROUP,
    },
    {
        id: 'elevator',
        label: translate('Ascenseur'),
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'balcony',
        label: translate('Balcon'),
        subField: {
            id: 'balcony_area',
            component: (
                <Field
                    component={Postfix}
                    postfixSymbol="m2"
                    type="number"
                    name="balcony_area"
                    small
                />
            ),
        },
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'terrace',
        label: translate('Terrasse'),
        subField: {
            id: 'terrace_area',
            component: (
                <Field
                    component={Postfix}
                    postfixSymbol="m2"
                    type="number"
                    name="terrace_area"
                    small
                />
            ),
        },
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'cellar',
        label: translate('Cave'),
        subField: {
            id: 'cellar_count',
            component: (
                <Field component={Counter} fieldLogic={OneToTenLogic} name="cellar_count" small />
            ),
        },
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'basement',
        label: translate('Sous-sol'),
        subField: {
            id: 'basement_area',
            component: (
                <Field
                    component={Postfix}
                    postfixSymbol="m2"
                    type="number"
                    name="basement_area"
                    small
                />
            ),
        },
        group: PROPERTY_HOUSE_GROUP,
    },
    {
        id: 'parking',
        label: translate('Places de parking'),
        subField: {
            id: 'parking_count',
            component: (
                <Field component={Counter} fieldLogic={OneToTenLogic} name="parking_count" small />
            ),
        },
        group: PROPERTY_HOUSE_GROUP.concat(PROPERTY_APARTMENT_GROUP),
    },
    {
        id: 'secondary_building',
        label: translate('Bâtiments annexes'),
        subField: {
            id: 'secondary_building_count',
            component: (
                <Field
                    component={Counter}
                    fieldLogic={OneToTenLogic}
                    name="secondary_building_count"
                    small
                />
            ),
        },
        group: PROPERTY_HOUSE_GROUP,
    },
    {
        id: 'secondary_room',
        label: translate('Chambre de service'),
        subField: {
            id: 'secondary_room_count',
            component: (
                <Field
                    component={Counter}
                    fieldLogic={OneToTenLogic}
                    name="secondary_room_count"
                    small
                />
            ),
        },
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'exceptional_view',
        label: translate('Vue exceptionnelle'),
        sublabel: translate('(Ex : Tour Eiffel, mer, montagne)'),
        group: PROPERTY_HOUSE_GROUP.concat(PROPERTY_APARTMENT_GROUP),
    },
    {
        id: 'swimming_pool',
        label: translate('Piscine'),
        group: PROPERTY_HOUSE_GROUP,
    },
    {
        id: 'sewerage',
        label: translate('Raccordement au réseau d’évacuation des eaux usées'),
        group: PROPERTY_HOUSE_GROUP,
    },
    {
        id: 'renovated_shared_parts',
        label: translate('Parties communes rénovées'),
        sublabel: translate('(depuis - de trois ans)'),
        group: PROPERTY_APARTMENT_GROUP,
    },
    {
        id: 'renovated_facade',
        label: translate('Ravalement de façade récent'),
        sublabel: translate('(depuis - de trois ans)'),
        group: PROPERTY_APARTMENT_GROUP,
    },
];

/**
 * Ground garden private value.
 * @type {String}
 */
export const GROUND_GARDEN_PRIVATE = 'GROUND_GARDEN.PRIVATE';

/**
 * Ground Garden Options.
 * @type {Array}
 */
export const GROUND_GARDEN_OPTIONS = [
    {
        value: 'GROUND_GARDEN.NONE',
        label: 'Non',
    },
    {
        value: GROUND_GARDEN_PRIVATE,
        label: 'Oui, privé',
    },
    {
        value: 'GROUND_GARDEN.SHARED',
        label: 'Oui, collectif',
    },
];

/**
 * Average relative perception (default value).
 * @type {String}
 */
export const AVERAGE_RELATIVE_PERCEPTION = 'RELATIVE_PERCEPTION.AVERAGE';

/**
 * Relative perceptions.
 * @type {Array}
 */
export const RELATIVE_PERCEPTIONS = [
    { value: 'RELATIVE_PERCEPTION.LOW', label: translate('Inférieure') },
    { value: AVERAGE_RELATIVE_PERCEPTION, label: translate('Comparable') },
    { value: 'RELATIVE_PERCEPTION.HIGH', label: translate('Supérieure') },
];

/**
 * Standard property standing (default value).
 * @type {String}
 */
export const AVERAGE_STANDING = 'STANDING.AVERAGE';

/**
 * Propertys standing.
 * @type {Array}
 */
export const STANDING_VALUES = [
    { value: AVERAGE_STANDING, label: translate('Standard') },
    { value: 'STANDING.HIGH', label: translate('Élevé') },
];

/**
 * Average lightness (default value).
 * @type {String}
 */
export const AVERAGE_LIGHTNESS = 'LIGHTNESS.AVERAGE';

/**
 * Lightnesses.
 * @type {Array}
 */
export const LIGHTNESS_VALUES = [
    { value: 'LIGHTNESS.VERY_LOW', label: translate('Sombre') },
    { value: 'LIGHTNESS.LOW', label: translate('Peu clair') },
    { value: AVERAGE_LIGHTNESS, label: translate('Standard') },
    { value: 'LIGHTNESS.HIGH', label: translate('Clair') },
    { value: 'LIGHTNESS.VERY_HIGH', label: translate('Très clair') },
];

/**
 * Average peacefulness (default value).
 * @type {String}
 */
export const AVERAGE_PEACEFULNESS = 'PEACEFULNESS.AVERAGE';

/**
 * Peacefulnesses.
 * @type {Array}
 */
export const PEACEFULNESS_VALUES = [
    { value: 'PEACEFULNESS.VERY_LOW', label: translate('Très bruyant') },
    { value: 'PEACEFULNESS.LOW', label: translate('Bruyant') },
    { value: AVERAGE_PEACEFULNESS, label: translate('Standard') },
    { value: 'PEACEFULNESS.HIGH', label: translate('Calme') },
    { value: 'PEACEFULNESS.VERY_HIGH', label: translate('Très calme') },
];

/**
 * Average peacefulness (default value).
 * @type {String}
 */
export const AVERAGE_ROOF = 'ROOF_QUALITY.AVERAGE';

/**
 * Roof values.
 * @type {Array}
 */
export const ROOF_VALUES = [
    { value: 'ROOF_QUALITY.LOW', label: 'À rénover' },
    { value: AVERAGE_ROOF, label: 'Standard' },
    { value: 'ROOF_QUALITY.HIGH', label: 'Refaite à neuf' },
];

/**
 * Build periods.
 * @type {Array}
 */
export const BUILD_PERIODS = [
    { value: 1849, label: 'Avant 1850' },
    { value: 1851, label: 'De 1850 à 1913' },
    { value: 1915, label: 'De 1914 à 1947' },
    { value: 1949, label: 'De 1948 à 1969' },
    { value: 1971, label: 'De 1970 à 1980' },
    { value: 1982, label: 'De 1981 à 1991' },
    { value: 1993, label: 'De 1992 à 2000' },
    { value: 2002, label: 'De 2001 à 2010' },
    { value: 2012, label: 'Après 2011' },
    { value: -2, label: 'Je ne sais pas' },
];

/**
 * Energy consumptions.
 * @type {Array}
 */
export const ENERGY_CONSUMPTIONS = [
    { value: 'ENERGY_CONSUMPTION.A', label: 'A' },
    { value: 'ENERGY_CONSUMPTION.B', label: 'B' },
    { value: 'ENERGY_CONSUMPTION.C', label: 'C' },
    { value: 'ENERGY_CONSUMPTION.D', label: 'D' },
    { value: 'ENERGY_CONSUMPTION.E', label: 'E' },
    { value: 'ENERGY_CONSUMPTION.F', label: 'F' },
    { value: 'ENERGY_CONSUMPTION.G', label: 'G' },
    { value: '-2', label: 'Je ne sais pas' },
];

/**
 * Average transport proximity (default value)
 * @type {String}
 */
export const AVERAGE_RENOVATION_LEVEL = 'RENOVATION_LEVEL.STANDARD';

/**
 * Revonation levels
 * @type {Array}
 */
export const RENOVATION_LEVELS = [
    { value: 'RENOVATION_LEVEL.GOOD_AS_NEW', label: 'Refait à neuf' },
    { value: 'RENOVATION_LEVEL.STANDARD', label: 'Standard' },
    { value: 'RENOVATION_LEVEL.LIGHT_WORK_REQUIRED', label: 'Rafraîchissement nécessaire' },
    { value: 'RENOVATION_LEVEL.HEAVY_WORK_REQUIRED', label: 'Travaux importants nécessaires' },
];

/**
 * Average transport proximity (default value).
 * @type {String}
 */
export const AVERAGE_TRANSPORT_PROXIMITY = 'TRANSPORT_PROXIMITY.AVERAGE';

/**
 * Transport proximities.
 * @type {Array}
 */
export const TRANSPORT_PROXIMITIES = [
    { value: 'TRANSPORT_PROXIMITY.VERY_LOW', label: 'Très éloigné' },
    { value: 'TRANSPORT_PROXIMITY.LOW', label: 'Éloigné' },
    { value: AVERAGE_TRANSPORT_PROXIMITY, label: 'Standard' },
    { value: 'TRANSPORT_PROXIMITY.HIGH', label: 'Proche' },
    { value: 'TRANSPORT_PROXIMITY.VERY_HIGH', label: 'Très proche' },
];

/**
 * Month values.
 * @type {Array}
 */
export const MONTH_VALUES = [
    { value: 1, label: translate('Janvier') },
    { value: 2, label: translate('Février') },
    { value: 3, label: translate('Mars') },
    { value: 4, label: translate('Avril') },
    { value: 5, label: translate('Mai') },
    { value: 6, label: translate('Juin') },
    { value: 7, label: translate('Juillet') },
    { value: 8, label: translate('Août') },
    { value: 9, label: translate('Septembre') },
    { value: 10, label: translate('Octobre') },
    { value: 11, label: translate('Novembre') },
    { value: 12, label: translate('Décembre') },
];

/**
 * Compute years values.
 * @return {Array} List of availables years
 */
function computeYears(min) {
    const currentYear = moment().year();
    const values = [];

    for (let value = currentYear; value >= min; value -= 1) {
        values.push({ value, label: String(value) });
    }

    return values;
}

/**
 * Ownership years values.
 * @type {Array}
 */
export const OWNERSHIP_YEAR_VALUES = computeYears(1900);

/**
 * Constructions works years values.
 * @type {Array}
 */
export const WORKS_YEAR_VALUES = computeYears(1975);

/**
 * Buy ownership.
 * @type {String}
 */
export const OWNERSHIP_BUY = 'OWNERSHIP_TYPE.BUY';

/**
 * Donation ownership.
 * @type {String}
 */
export const OWNERSHIP_DONATION = 'OWNERSHIP_TYPE.DONATION';

/**
 * Inheritance ownership.
 * @type {String}
 */
export const OWNERSHIP_INHERITANCE = 'OWNERSHIP_TYPE.INHERITANCE';

/**
 * Other ownership.
 * @type {String}
 */
export const OWNERSHIP_OTHER = 'OWNERSHIP_TYPE.OTHER';

/**
 * Ownership value.
 * @type {Array}
 */
export const OWNERSHIP_VALUES = [
    { value: OWNERSHIP_BUY, label: 'Achat' },
    { value: OWNERSHIP_DONATION, label: 'Donation' },
    { value: OWNERSHIP_INHERITANCE, label: 'Succession' },
    { value: OWNERSHIP_OTHER, label: 'Autre' },
];

/**
 * Type of residence
 * @type {Array}
 */
export const PROFILE_RESIDENCE_TYPE = [
    { value: 'RESIDENCE_TYPE.MAIN', label: 'Une résidence principale' },
    { value: 'RESIDENCE_TYPE.SECONDARY', label: 'Une résidence secondaire' },
    { value: 'RESIDENCE_TYPE.INVESTMENT', label: 'Une résidence locative' },
    { value: 'RESIDENCE_TYPE.OTHER', label: 'Autre' },
];

/** Rent residence */
export const RESIDENCE_TYPE_INVESTMENT = 'RESIDENCE_TYPE.INVESTMENT';

/**
 * Profile who started selling.
 * @type {String}
 */
export const PROFILE_STARTED_SELLING = 'SELLER_PROFILE.STARTED_SELLING';

/**
 * Profile who sells asap.
 * @type {String}
 */
export const PROFILE_SELL_ASAP = 'SELLER_PROFILE.SELL_ASAP';

/**
 * Profile who sells in three month.
 * @type {String}
 */
export const PROFILE_SELL_IN_THREE_MONTHS = 'SELLER_PROFILE.SELL_IN_THREE_MONTHS';

/**
 * Profile who sells in six months.
 * @type {String}
 */
export const PROFILE_SELL_IN_SIX_MONTHS = 'SELLER_PROFILE.SELL_IN_SIX_MONTHS';

/**
 * Profile who sells in more than six months.
 * @type {String}
 */
export const PROFILE_SELL_IN_MORE_THAN_SIX_MONTHS = 'SELLER_PROFILE.SELL_IN_MORE_THAN_SIX_MONTHS';

/**
 * Profile who  doesn't sell.
 * @type {String}
 */
export const PROFILE_NO_SALE_PROJECT = 'SELLER_PROFILE.NO_SALE_PROJECT';

/**
 * Profile seller
 * @type {Array}
 */
export const PROFILE_SELLER = [
    { value: PROFILE_STARTED_SELLING, label: "Oui, j'ai déjà commencé la vente" },
    { value: PROFILE_SELL_ASAP, label: 'Oui, dès que possible' },
    { value: PROFILE_SELL_IN_THREE_MONTHS, label: "Oui, d'ici 3 mois" },
    { value: PROFILE_SELL_IN_SIX_MONTHS, label: "Oui, d'ici 6 mois" },
    { value: PROFILE_SELL_IN_MORE_THAN_SIX_MONTHS, label: 'Oui, dans plus de 6 mois' },
    { value: PROFILE_NO_SALE_PROJECT, label: "Non, je n'ai pas de projet de vente" },
    { value: 'SELLER_PROFILE.SOLD', label: 'Non, je viens de le vendre' },
    { value: 'SELLER_PROFILE.RENT_PROJECT', label: 'Non, je souhaite mettre en location' },
];

/** Profile seller sold **/
export const SELLER_PROFILE_SOLD = 'SELLER_PROFILE.SOLD';

/**
 * Profile sell method
 * @type {Array}
 */
export const PROFILE_SALE_METHOD = [
    { value: 'SALE_METHOD.CONTRACT', label: "Avec l'aide d'agences immobilières" },
    { value: 'SALE_METHOD.SOLO', label: 'Par mes propres moyens' },
    { value: 'SALE_METHOD.CONTRACT_AND_SOLO', label: 'Les deux' },
];

/**
 * Buyer profile who want to sell.
 * @type {String}
 */
export const PROFILE_BUYER_BUY = 'BUYER_PROFILE.BUY';

/**
 * Profile buyer
 * @type {Array}
 */
export const PROFILE_BUYER = [
    { value: PROFILE_BUYER_BUY, label: 'Je souhaite acheter' },
    { value: 'BUYER_PROFILE.SELL', label: 'Je viens de vendre' },
    { value: 'BUYER_PROFILE_PROSPECTIVE_TENANT', label: 'Je souhaite le louer' },
    { value: 'BUYER_PROFILE_EXISTING_TENANT', label: "J'en suis locataire" },
    { value: 'BUYER_PROFILE.TOURIST', label: "Je m'informe" },
];

export const BUYER_PROFILE_SELL = 'BUYER_PROFILE.SELL';

/**
 * Profile incentive
 * @type {Array}
 */
export const PROFILE_INCENTIVE = [
    { value: 'BUY_INCENTIVE.FORMER_PROPERTY', label: 'Ancien' },
    { value: 'BUY_INCENTIVE.NEW_PROPERTY', label: 'Neuf' },
    { value: 'BUY_INCENTIVE.FORMER_OR_NEW_PROPERTY', label: 'Ancien et neuf' },
];

/**
 * Profile needs loan status
 * @type {Array}
 */
export const PROFILE_NEEDS_LOAN_STATUS = [
    { value: 'CONTACT_PROFILE.NEEDS_LOAN_STATUS.WONDERING', label: "Je m'informe" },
    { value: 'CONTACT_PROFILE.NEEDS_LOAN_STATUS.STARTING', label: 'Je débute' },
    {
        value: 'CONTACT_PROFILE.NEEDS_LOAN_STATUS.ACTIVE',
        label: 'Je recherche activement (+ de cinq visites)',
    },
    {
        value: 'CONTACT_PROFILE.NEEDS_LOAN_STATUS.DONE',
        label: "J'ai trouvé un bien et ai fait une offre",
    },
];

/**
 * Profile needs to sell
 * @type {Array}
 */
export const PROFILE_NEEDS_TO_SELL = [
    {
        value: 'BUYER_SELL_PROFILE.ALREADY_STARTED_TO_SELL',
        label: "Oui, j'ai déjà commencé à vendre",
    },
    {
        value: 'BUYER_SELL_PROFILE.NOT_STARTED_SELLING',
        label: "Oui, mais je n'ai pas commencé à vendre",
    },
    { value: 'BUYER_SELL_PROFILE.NO_PLAN_TO_SELL', label: 'Non' },
];

/**
 * User profile
 * @type {Array}
 */
export const USER_PROFILE = [
    { value: 'USER_PROFILE.REALTOR', label: 'Agent immobilier' },
    { value: 'USER_PROFILE.REPRESENTATIVE', label: 'Mandataire immobilier' },
    { value: 'USER_PROFILE.SYNDIC', label: 'Administrateur de bien / Syndic' },
    { value: 'USER_PROFILE.INSURER', label: 'Assureur' },
    { value: 'USER_PROFILE.BANKER', label: 'Banquier' },
    { value: 'USER_PROFILE.ASSET_ADVISOR', label: 'Conseiller en Gestion de Patrimoine' },
    { value: 'USER_PROFILE.BROKER', label: 'Courtier' },
    { value: 'USER_PROFILE.DIAGNOSER', label: 'Diagnostiqueur' },
    { value: 'USER_PROFILE.REAL_ESTATE_EXPERT', label: 'Expert immobilier' },
    { value: 'USER_PROFILE.INSTITUTIONNAL', label: 'Institutionnel / Fondation' },
    { value: 'USER_PROFILE.NOTARY', label: 'Notaire' },
    { value: 'USER_PROFILE.ESTATE_DEVELOPER', label: 'Promoteur / Constructeur' },
    { value: 'USER_PROFILE.OTHER', label: 'Autre' },
];

/**
 * Use profile realtor positions
 * @type {Array}
 */
export const USER_PROFILE_REALTOR_POSITIONS = [
    { value: 'USER_PROFILE_REALTOR_POSITIONS.PRESIDENT', label: 'Président / Gérant' },
    { value: 'USER_PROFILE_REALTOR_POSITIONS.DIRECTOR', label: "Directeur / Responsable d'agence" },
    {
        value: 'USER_PROFILE_REALTOR_POSITIONS.ADVISOR_SALES',
        label: 'Conseiller / Négociateur de vente',
    },
    {
        value: 'USER_PROFILE_REALTOR_POSITIONS.ADVISOR_RENT',
        label: 'Conseiller / Négociateur de location',
    },
    {
        value: 'USER_PROFILE_REALTOR_POSITIONS.ADMINISTRATOR_RENT',
        label: 'Gestionnaire location / Syndic',
    },
    { value: 'USER_PROFILE_REALTOR_POSITIONS.ASSISTANT', label: 'Assistant' },
    { value: 'USER_PROFILE_REALTOR_POSITIONS.OTHER', label: 'Autres' },
];

/**
 * Gender values for login form.
 * @type {Array}
 */
export const GENDER_VALUES = [
    { value: 1, label: translate('Monsieur') },
    { value: 3, label: translate('Madame') },
];

/**
 * Partners offers.
 * @type {Array}
 */
export const PARTNERS_OFFERS_VALUES = [
    { value: 1, label: 'OUI, avec plaisir' },
    { value: 2, label: "OUI, mais pas plus d'1 fois / mois" },
    { value: 3, label: 'OUI, mais pas plus de 2 fois / mois' },
    { value: 0, label: "NON, j'y renonce" },
];

/**
 * Map zoom when center on a city
 * @type {number}
 */
export const ZOOM_FOR_CITY = 12;

/**
 * Map zoom when center on an address
 * @type {number}
 */
export const ZOOM_FOR_ADDRESS = 15;

/**
 * Coordinates of the center of the France
 * @type {{lat: number, lng: number}}
 */
export const FRANCE_COORDINATES = {
    lat: 46.800059446788595,
    lng: 1.9995117187447704,
};

/**
 * Geotype id for api calls
 * @type {string}
 */
export const GEOTYPE_CITIES = 'cities';

/**
 * Geotype id for api calls
 * @type {string}
 */
export const GEOTYPE_ARRMUNS = 'arrmuns';

/**
 * Geotype id for api calls
 * @type {string}
 */
export const GEOTYPE_SUBCITIES = 'subcities';

/**
 * Geotype id for api calls
 * @type {string}
 */
export const GEOTYPE_ADDRESSES = 'addresses';

/**
 * Geotype label address of a place
 * @type {string}
 */
export const ADDRESS_GEOTYPE_LABEL = 'address';

/**
 * Geotype label street of a place
 * @type {string}
 */
export const STREET_GEOTYPE_LABEL = 'street';

/**
 * Geotype label borough of a place
 * @type {string}
 */
export const BOROUGH_GEOTYPE_LABEL = 'borough';

/**
 * Geotype label neighborhood of a place
 * @type {string}
 */
export const NEIGHBORHOOD_GEOTYPE_LABEL = 'neighborhood';

/**
 * Keep user choice id in address step.
 * @type {String}
 */
export const KEEP_USER_ADDRESS_CHOICE = 'KEEP_USER_ADDRESS_CHOICE';

/**
 * Origin of the estimate: estima funnel (standard way)
 */
export const ORIGIN_ESTIMA_FUNNEL = 'ESTIMA_FUNNEL';
